import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { formatCurrency, useEffectOnce, usePathParam } from '@chilecompra/react-kit'
import { Card, Dialog, Skeleton, Typography, Table, TableRow } from '@chilecompra/react-kit/components'
import { inIframe } from '@chilecompra/react-kit/utils'
import { SCORE_RANGE_COLORS, USER_TYPE } from '../../config/settings/constant'
import {
  REACT_APP_MERCADO_PUBLICO_BASE_URL,
  REACT_APP_OPEN_DATA_INFORMATION_URL,
  REACT_APP_OPEN_DATA_CONTRACTS_SELLER
} from '../../config/settings/environment'
import { onGetSummarySectionInformationThunk, onGetOpenDataInformationThunk } from './SummarySection.actions'
import { useScore } from '../../components/Score/Score.hooks'
import ProtectByFeatures from '../../components/Security/ProtectByFeatures'
import Score from '../../components/Score/Score'
import {
  Box,
  ContentWrapper,
  DialogContent,
  DialogMessage,
  Link,
  Separator,
  Toast,
  TableCell,
  TableContainer
} from './SummarySection.styles'
import { onGetContractualBehaviorSummaryByReviewsThunk } from '../ContractualBehavior/ContractualBehavior.actions'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'

/**
 * The summary section container.
 */
const SummarySection = () => {
  const { userType } = useAuthorization()
  const dispatch = useDispatch()
  const dni = usePathParam('rut')
  const [showDialog, setShowDialog] = useState(false)
  const { awardedAgreements, contractualBehavior, error, loading, openDataLoading, openDataError, salesLastMonths } =
    useSelector(state => state.summarySection)
  const { errorByReviews, loadingByReviews, months, rateByReviews, receivedContracts, requestedByReviews } =
    useSelector(state => state.contractualBehavior)
  const { error: sellerError } = useSelector(state => state.basicSellerInformation)

  const { color, updatedRate } = useScore({
    rangeValues: SCORE_RANGE_COLORS,
    rate: contractualBehavior.rate,
    max: contractualBehavior.max
  })

  const { color: colorByReviews, updatedRate: updatedRateByReviews } = useScore({
    rangeValues: SCORE_RANGE_COLORS,
    rate: rateByReviews,
    max: contractualBehavior.max
  })

  useEffectOnce(() => {
    if (!loading) {
      return
    }
    dispatch(onGetSummarySectionInformationThunk({ dni }))
  })

  useEffectOnce(() => {
    if (!loadingByReviews || requestedByReviews) return

    dispatch(onGetContractualBehaviorSummaryByReviewsThunk({ dni }))
  })

  useEffectOnce(() => {
    if (!openDataLoading) {
      return
    }
    dispatch(onGetOpenDataInformationThunk({ dni }))
  })

  const isInIframe = useMemo(() => {
    return inIframe()
  }, [])

  const handleOpenModalCallback = () => {
    if (isInIframe) {
      window.parent.postMessage(
        {
          action: 'scrollTo',
          value: {
            x: 0,
            y: 0
          }
        },
        REACT_APP_MERCADO_PUBLICO_BASE_URL
      )
    }
  }
  const onToggleDialog = action => () => setShowDialog(action)

  return (error && error?.reason === 'FORBIDDEN_ERROR') || sellerError ? null : (
    <Card elevation="1" margin="14px 0 0" padding="0">
      <Box padding="22px 14px 10px">
        <Typography align="right" fontWeight="bold" variant="caption">
          Ventas en Mercado Público
        </Typography>
        {!openDataError && (
          <Typography align="right" fontSize="30px" fontWeight="bold" variant="h1">
            {openDataLoading ? <Skeleton width="100%" /> : formatCurrency(salesLastMonths.amount, '$')}
          </Typography>
        )}
        {!openDataError && (
          <Typography align="right" fontSize="13px" italic variant="subtitle">
            {openDataLoading ? <Skeleton width="75%" /> : salesLastMonths.period}
          </Typography>
        )}
        {!openDataLoading && openDataError && (
          <Typography align="right" fontWeight="bold" variant="h4">
            Sin información
          </Typography>
        )}
        <ProtectByFeatures rule={features => features['com.seller/link.to.open.data.information']}>
          <Toast severity="info" fontSize="12px" margin="15px 0 20px 27px" padding="0 12px">
            Revisa más ventas de este proveedor en{' '}
            <Link
              color="primary"
              display="inline"
              href={`${REACT_APP_OPEN_DATA_INFORMATION_URL}${dni}`}
              rel="noreferrer"
              target="_blank"
              textDecoration="underline"
            >
              Datos Abiertos.
            </Link>
          </Toast>
        </ProtectByFeatures>
      </Box>

      <Separator />

      <ProtectByFeatures
        rule={features =>
          features[
            userType !== USER_TYPE.PUBLIC ? 'com.seller/contractual.behavior' : 'com.seller/contractual.behavior.public'
          ]
        }
      >
        <Box padding="10px 14px">
          <Typography align="right" fontWeight="bold" variant="caption">
            Comportamiento Base
          </Typography>

          {!error && !contractualBehavior.hasNoRate && (
            <Score color={color} loading={loading} max={contractualBehavior.max} rate={updatedRate} />
          )}

          {(contractualBehavior.rate === 0 || contractualBehavior.rate === -1) && (
            <ContentWrapper alignItems="center" justifyContent="flex-end" width="150px">
              {loading && <Skeleton padding="0 8px" variant="rect" width="75%" />}

              {!loading && !error && (
                <ContentWrapper flexDirection="column">
                  <Typography align="right" fontWeight="bold" variant="h4">
                    Sin información
                  </Typography>

                  {contractualBehavior.rate === 0 && (
                    <Typography align="right" variant="subtitle">
                      {`Este proveedor no ha recibido órdenes de compra en ${
                        months === 1 ? 'el último mes' : `los últimos ${months} meses`
                      }.`}
                    </Typography>
                  )}
                </ContentWrapper>
              )}
            </ContentWrapper>
          )}

          {!loading && error && (
            <Typography align="right" fontWeight="bold" variant="h4">
              Sin información
            </Typography>
          )}

          {contractualBehavior.rate !== 0 && (
            <Typography align="right" fontSize="13px" italic variant="subtitle">
              {loading ? (
                <Skeleton width="75%" />
              ) : (
                `Nota calculada según sanciones recibidas en ${
                  months === 1 ? 'el último mes' : `los últimos ${months} meses`
                }`
              )}
            </Typography>
          )}
        </Box>

        <Separator />

        <Box padding="10px 14px">
          <Typography align="right" fontWeight="bold" variant="caption">
            Comportamiento por evaluación de contratos
          </Typography>

          {!loadingByReviews && receivedContracts !== 0 && (
            <Score
              color={colorByReviews}
              loading={loadingByReviews}
              max={contractualBehavior.max}
              rate={updatedRateByReviews}
            />
          )}

          {receivedContracts !== 0 && (
            <Typography align="right" fontSize="13px" italic variant="subtitle">
              {loading ? (
                <Skeleton width="75%" />
              ) : (
                `Nota calculada según contratos evaluados en ${
                  months === 1 ? 'el último mes' : `los últimos ${months} meses`
                }`
              )}
            </Typography>
          )}

          {receivedContracts === 0 && (
            <ContentWrapper alignItems="center" justifyContent="flex-end" width="150px">
              {loadingByReviews && <Skeleton padding="0 8px" variant="rect" width="75%" />}

              {!loadingByReviews && receivedContracts === 0 && !errorByReviews && (
                <ContentWrapper flexDirection="column">
                  <Typography align="right" fontWeight="bold" variant="h4">
                    Sin información
                  </Typography>

                  <Typography align="right" variant="subtitle">
                    {`Este proveedor no ha recibido evaluaciones en ${
                      months === 1 ? 'el último mes' : `los últimos ${months} meses`
                    }.`}
                  </Typography>
                </ContentWrapper>
              )}
            </ContentWrapper>
          )}

          {!loadingByReviews && errorByReviews && (
            <Typography align="right" fontWeight="bold" variant="h4">
              Sin información
            </Typography>
          )}
        </Box>

        <ProtectByFeatures rule={features => features['com.seller/link.to.open.contract.seller']}>
          <Toast severity="info" fontSize="12px" margin="15px 0 20px 27px" padding="0 12px">
            Revisa todos los contratos de este proveedor en{' '}
            <Link
              color="primary"
              display="inline"
              href={`${REACT_APP_OPEN_DATA_CONTRACTS_SELLER}`}
              rel="noreferrer"
              target="_blank"
              textDecoration="underline"
            >
              Gestión de Contratos.
            </Link>
          </Toast>
        </ProtectByFeatures>
        <Separator />
      </ProtectByFeatures>

      <Box padding="10px 14px 14px">
        <Typography align="right" fontWeight="bold" variant="caption">
          Convenios Marco Adjudicados
        </Typography>

        {!error && (
          <Typography align="right" fontSize="30px" fontWeight="bold" variant="h1">
            {loading ? <Skeleton width="40px" /> : awardedAgreements.length}
          </Typography>
        )}
        {!error && !loading && awardedAgreements.length > 0 && (
          <>
            <Link display="block" italic textAlign="right" textDecoration="underline" onClick={onToggleDialog(true)}>
              Ver listado de convenios
            </Link>

            <Dialog
              open={showDialog}
              position={isInIframe ? 'absolute' : 'relative'}
              onClose={onToggleDialog(false)}
              onOpenCallback={handleOpenModalCallback}
              margin="auto"
              maxWidth="600px"
              padding="16px"
            >
              <DialogContent>
                <DialogMessage>
                  <Typography fontWeight="bold" margin="0 0 16px" variant="h4">
                    Convenios Marco adjudicados por el proveedor
                  </Typography>
                  <TableContainer>
                    <Table accessibility={{ label: 'Sanciones históricas' }} enableStripped>
                      <TableRow enableHead>
                        <TableCell padding="20px 16px" width="300px">
                          <Typography fontWeight="bold" variant="caption">
                            Convenios
                          </Typography>
                        </TableCell>
                        <TableCell padding="20px 16px" width="100px">
                          <Typography fontWeight="bold" variant="caption">
                            UTP
                          </Typography>
                        </TableCell>
                      </TableRow>
                      {awardedAgreements.map((item, index) => {
                        return (
                          <TableRow key={`agreement-${index}`}>
                            <TableCell disableBottomBorder padding="16px" width="300px">
                              {item.nombreConvenioMarco}
                            </TableCell>
                            <TableCell disableBottomBorder padding="16px" width="100px">
                              {item.esUTP ? 'SI' : 'NO'}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </Table>
                  </TableContainer>
                </DialogMessage>
              </DialogContent>
            </Dialog>
          </>
        )}

        {awardedAgreements.length === 0 && error && (
          <Typography align="right" fontWeight="bold" variant="h4">
            Sin información
          </Typography>
        )}
      </Box>
    </Card>
  )
}

export default SummarySection
